import {
  hasUrlOrTaskQuestion,
  resolveMessageDisplayText,
} from '@/shared-question-model';

import { DELIVERY_CHANNELS } from './shared-responder-v2-core-handsfree.types';
import { resolveStepLabel } from './shared-responder-v2-core-label.helpers';
import {
  RESPONDER_PHASES,
  RESPONSE_MODES,
  DEVICE_GATE_VERDICTS,
} from './shared-responder-v2-core-session.consts';
import type {
  ResponderCapabilities,
  ResponderSessionState,
  ResponderStep,
} from './shared-responder-v2-core-session.types';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  SITE_VISITS,
  THREAD_KINDS,
  THREAD_STATUSES,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderThread,
} from './shared-responder-v2-core-thread.types';

export function createThread(spec: QuestionSpec): ResponderThread {
  return {
    questionId: spec.questionId,
    kind: spec.kind,
    threadId: null,
    status: THREAD_STATUSES.Idle,
    draft: getThreadBehaviour(spec.kind).emptyDraft(spec),
    followUpDraft: null,
    answer: null,
    turns: [],
    followUpCount: 0,
    requestSeq: 0,
    isDistributionVisible: true,
    error: null,
    editRollback: null,
    answerMessageId: null,
    siteVisit: SITE_VISITS.Unopened,
    liveResults: null,
  };
}

/** Falls back to step 0 when `firstQuestionId` is `null` or absent from `steps`, instead of throwing. */
export function resolveInitialStepIndex(
  steps: readonly ResponderStep[],
  firstQuestionId: string | null
): number {
  if (firstQuestionId === null) return 0;

  const index = steps.findIndex((step) => step.questionId === firstQuestionId);

  return index === -1 ? 0 : index;
}

function seedSkippedQuestionIds(
  steps: readonly ResponderStep[],
  startIndex: number,
  existing: readonly string[]
): readonly string[] {
  const seeded = steps
    .slice(0, startIndex)
    .map((step) => step.questionId)
    .filter((questionId) => !existing.includes(questionId));

  return seeded.length === 0 ? existing : [...existing, ...seeded];
}

/**
 * Reconciles the step index `buildResponderSessionState` defaulted to 0 against the
 * `firstQuestionId` `initiateSession` resolves with; returns the same reference if unchanged.
 * Steps before a server-chosen start were skipped by the server, so they are seeded as skipped
 * rather than left owed to the final-send pre-flight.
 */
export function applyServerFirstQuestion(
  state: ResponderSessionState,
  firstQuestionId: string | null
): ResponderSessionState {
  const index = resolveInitialStepIndex(state.steps, firstQuestionId);

  if (index === state.currentStepIndex) return state;

  return {
    ...state,
    currentStepIndex: index,
    maxReachedStepIndex: index,
    resumeStepIndex: index,
    skippedQuestionIds: seedSkippedQuestionIds(
      state.steps,
      index,
      state.skippedQuestionIds
    ),
  };
}

export function createStep(
  spec: QuestionSpec,
  aiShortTitle: string | null = null
): ResponderStep {
  const questionText =
    spec.kind === THREAD_KINDS.Message
      ? resolveMessageDisplayText(spec.text)
      : spec.text;

  return {
    questionId: spec.questionId,
    kind: spec.kind,
    label: resolveStepLabel({ aiShortTitle, questionText }),
    isRequired: spec.isRequired,
    hasBranching: spec.hasBranching,
    showLiveDistribution: spec.showLiveDistribution,
  };
}

function resolveSessionMobileAppEmbedUrl(
  specs: readonly QuestionSpec[]
): string | null {
  const spec = specs.find((candidate) => candidate.mobileAppEmbedUrl !== null);

  return spec?.mobileAppEmbedUrl ?? null;
}

/**
 * Builds a session from question specs; shared by reducer specs, the dev driver and Storybook.
 * `hasUrlOrTask` is derived from specs, not passed as a flag, so progress-list visibility and
 * the voice-suppression rule can't disagree.
 */
export function buildResponderSessionState({
  specs,
  capabilities,
  shortTitles = {},
  firstQuestionId = null,
}: {
  specs: readonly QuestionSpec[];
  capabilities: Omit<ResponderCapabilities, 'hasBranching' | 'hasUrlOrTask'>;
  shortTitles?: Readonly<Record<string, string>>;
  /** The server-named question to start on; `null` starts on the first step. */
  firstQuestionId?: string | null;
}): ResponderSessionState {
  const steps = specs.map((spec) =>
    createStep(spec, shortTitles[spec.questionId] ?? null)
  );

  const specsById: Record<string, QuestionSpec> = {};
  const threads: Record<string, ResponderThread> = {};

  for (const spec of specs) {
    specsById[spec.questionId] = spec;
    threads[spec.questionId] = createThread(spec);
  }

  const initialStepIndex = resolveInitialStepIndex(steps, firstQuestionId);

  return {
    phase: RESPONDER_PHASES.DeviceGate,
    deviceGate: DEVICE_GATE_VERDICTS.Allowed,
    capabilities: {
      ...capabilities,
      deliveryChannel: capabilities.deliveryChannel ?? DELIVERY_CHANNELS.Chat,
      hasBranching: specs.some((spec) => spec.hasBranching),
      hasUrlOrTask: hasUrlOrTaskQuestion(specs),
    },
    steps,
    specs: specsById,
    threads,
    currentStepIndex: initialStepIndex,
    maxReachedStepIndex: initialStepIndex,
    resumeStepIndex: initialStepIndex,
    nextQuestionId: null,
    isComplete: false,
    skippedQuestionIds: seedSkippedQuestionIds(steps, initialStepIndex, []),
    editing: { mode: 'none' },
    terminalActions: [],
    lastOpenedSiteUrl: null,
    mobileAppEmbedUrl: resolveSessionMobileAppEmbedUrl(specs),
    mode: capabilities.allowVoice
      ? capabilities.projectInteractionType
      : RESPONSE_MODES.Text,
  };
}
